/**
 * Demo positions — IDs match DUMMY_SESSIONS.campaign_id so Dashboard /
 * Candidates / Interviews / All Positions stay consistent.
 */
import { DUMMY_SESSIONS } from "./dashboardDummyData";

function daysAgo(n) {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() - n);
  return d.toISOString();
}

function statsForCampaign(campaignId, sessions = DUMMY_SESSIONS) {
  const rows = sessions.filter((s) => String(s.campaign_id) === String(campaignId));
  const completed = rows.filter((s) => String(s.status).toLowerCase() === "completed");
  const inProgress = rows.filter((s) => String(s.status).toLowerCase() === "in_progress");
  const scores = completed
    .map((s) => Number(s.overall_score))
    .filter((n) => Number.isFinite(n) && n > 0);
  const avg = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
  return {
    total_candidates: rows.length,
    invited_candidates: rows.length,
    completed_interviews: completed.length,
    in_progress_interviews: inProgress.length,
    total_interviews: rows.length,
    avg_score: avg != null ? Math.round(avg * 10) / 10 : null,
    shortlisted_candidates: completed.filter((s) => Number(s.overall_score) >= 6.5).length,
    questions_count: rows.length > 0 ? 10 : 0,
  };
}

/** Base position definitions (status variety for All Positions filters). */
const POSITION_DEFS = [
  {
    id: "demo-camp-fe",
    name: "Senior Frontend Engineer",
    job_role: "Frontend",
    status: "active",
    is_active: true,
    language: "en",
    difficulty_level: "hard",
    created_at: daysAgo(12),
    source_tool: "wizetok",
  },
  {
    id: "demo-camp-be",
    name: "Backend Engineer",
    job_role: "Backend",
    status: "active",
    is_active: true,
    language: "en",
    difficulty_level: "medium",
    created_at: daysAgo(8),
    source_tool: "wizetok",
  },
  {
    id: "demo-camp-ds",
    name: "Data Scientist",
    job_role: "Data Science",
    status: "active",
    is_active: true,
    language: "es",
    difficulty_level: "hard",
    created_at: daysAgo(5),
    source_tool: "reachout",
  },
  {
    id: "demo-camp-pm",
    name: "Product Manager",
    job_role: "Product",
    status: "fulfilled",
    is_active: false,
    language: "en",
    difficulty_level: "medium",
    created_at: daysAgo(40),
  },
  {
    id: "demo-camp-draft-1",
    name: "Mobile Engineer (Draft)",
    job_role: "Mobile",
    status: "draft",
    is_active: false,
    language: "en",
    difficulty_level: "medium",
    created_at: daysAgo(2),
  },
  {
    id: "demo-camp-draft-2",
    name: "QA Automation Lead",
    job_role: "Quality Assurance",
    status: "draft",
    is_active: false,
    language: "en",
    difficulty_level: "easy",
    created_at: daysAgo(1),
  },
  {
    id: "demo-camp-hold-1",
    name: "DevOps Engineer",
    job_role: "Infrastructure",
    status: "on_hold",
    is_active: false,
    language: "en",
    difficulty_level: "hard",
    created_at: daysAgo(20),
  },
  {
    id: "demo-camp-hold-2",
    name: "UX Designer",
    job_role: "Design",
    status: "on_hold",
    is_active: false,
    language: "en",
    difficulty_level: "medium",
    created_at: daysAgo(15),
    source_tool: "codesense",
  },
  {
    id: "demo-camp-fulfilled-2",
    name: "Solutions Architect",
    job_role: "Architecture",
    status: "fulfilled",
    is_active: false,
    language: "en",
    difficulty_level: "hard",
    created_at: daysAgo(55),
  },
  {
    id: "demo-camp-archived-1",
    name: "Legacy Java Support",
    job_role: "Backend",
    status: "archived",
    is_active: false,
    language: "en",
    difficulty_level: "easy",
    created_at: daysAgo(90),
  },
  {
    id: "demo-camp-archived-2",
    name: "Intern — Summer 2025",
    job_role: "Internship",
    status: "archived",
    is_active: false,
    language: "en",
    difficulty_level: "easy",
    created_at: daysAgo(120),
  },
];

/** Positions with candidate/interview counts derived from the same DUMMY_SESSIONS. */
export const DUMMY_POSITIONS = POSITION_DEFS.map((p) => ({
  ...p,
  ...statsForCampaign(p.id),
}));

/** Lightweight campaign list for Dashboard / Analytics filters (same IDs). */
export const DUMMY_CAMPAIGNS = DUMMY_POSITIONS.map((p) => ({
  id: p.id,
  name: p.name,
  job_role: p.job_role,
  is_active: !!p.is_active,
  status: p.status,
}));

export function computeDummyStatusCounts(rows) {
  const byStatus = (s) => rows.filter((c) => (c.status || "active") === s).length;
  return {
    all: rows.length,
    active: byStatus("active"),
    draft: byStatus("draft"),
    on_hold: byStatus("on_hold"),
    fulfilled: byStatus("fulfilled"),
    archived: byStatus("archived"),
  };
}

export function getDemoPositionById(id) {
  return DUMMY_POSITIONS.find((p) => String(p.id) === String(id)) || null;
}

export function getDemoSessionsForCampaign(campaignId) {
  return DUMMY_SESSIONS.filter((s) => String(s.campaign_id) === String(campaignId));
}
